/**
 * Global Video Download Manager
 * Manages video generation polling that persists across page navigation (within same session)
 * Automatically downloads videos when ready, even if user navigates away
 */

import logger from './logger';

class VideoDownloadManager {
  constructor() {
    this.activeDownloads = new Map(); // taskId -> { audiobookId, title, pollInterval, callback }
    this.listeners = []; // Components that want to know about active downloads
    this.audiobookLocks = new Set(); // Set of audiobookIds currently being processed (prevents duplicates)
  }

  /**
   * Start tracking a video generation
   * @param {string} audiobookId - Audiobook ID
   * @param {string} title - Audiobook title
   * @param {Function} apiClient - API client for polling
   * @param {Function} onComplete - Callback when video is ready (receives video_url)
   * @returns {string} taskId or null if already polling
   */
  startVideoGeneration(audiobookId, title, apiClient, onComplete) {
    // CRITICAL: Check global lock FIRST before doing anything
    if (this.audiobookLocks.has(audiobookId)) {
      logger.log(`⚠️ Already polling for audiobook ${audiobookId}, skipping duplicate`);
      return null;
    }

    // Acquire lock immediately
    this.audiobookLocks.add(audiobookId);
    logger.log(`🔒 Acquired lock for audiobook ${audiobookId}`);

    const taskId = `video-${audiobookId}-${Date.now()}`;

    logger.log(`🎬 Starting video generation tracking: ${taskId}`);

    let pollCount = 0;
    const maxPolls = 180; // 6 minutes max
    let hasCompleted = false; // Guard to prevent multiple completions

    const pollInterval = setInterval(async () => {
      if (hasCompleted) return; // Skip if already completed

      pollCount++;
      logger.log(`🔄 Polling for video (attempt ${pollCount}/${maxPolls})`);

      try {
        const response = await apiClient.getAudiobook(audiobookId);

        if (response.status && response.data?.video_url) {
          // Video is ready!
          hasCompleted = true; // Set guard immediately
          logger.log(`✅ Video ready: ${response.data.video_url}`);

          // Clear interval FIRST before doing anything else
          clearInterval(pollInterval);

          // Stop polling and update status
          this.completeDownload(taskId, response.data.video_url);

          // Trigger download callback (only once)
          if (onComplete) {
            onComplete(response.data.video_url);
          }

          return;
        }
      } catch (error) {
        logger.error('Polling error:', error);
      }

      // Stop after max attempts
      if (pollCount >= maxPolls) {
        hasCompleted = true;
        clearInterval(pollInterval);
        logger.warn('Polling timeout reached');
        this.failDownload(taskId, 'Video generation timeout');
      }
    }, 2000); // Poll every 2 seconds

    // Store active download
    this.activeDownloads.set(taskId, {
      audiobookId,
      title,
      pollInterval,
      status: 'generating',
      startedAt: Date.now(),
    });

    // Notify listeners
    this.notifyListeners();

    return taskId;
  }

  /**
   * Complete a download (video is ready)
   */
  completeDownload(taskId, videoUrl) {
    const download = this.activeDownloads.get(taskId);
    if (!download) return;

    // Clear interval (safe to call multiple times)
    if (download.pollInterval) {
      clearInterval(download.pollInterval);
      download.pollInterval = null;
    }

    // Release lock
    this.audiobookLocks.delete(download.audiobookId);
    logger.log(`🔓 Released lock for audiobook ${download.audiobookId}`);

    // Update status
    download.status = 'completed';
    download.videoUrl = videoUrl;

    // Remove from active downloads after a delay (so UI can show completion)
    setTimeout(() => {
      this.activeDownloads.delete(taskId);
      this.notifyListeners();
    }, 3000);

    this.notifyListeners();
  }

  /**
   * Fail a download
   */
  failDownload(taskId, error) {
    const download = this.activeDownloads.get(taskId);
    if (!download) return;

    // Clear interval (safe to call multiple times)
    if (download.pollInterval) {
      clearInterval(download.pollInterval);
      download.pollInterval = null;
    }

    // Release lock
    this.audiobookLocks.delete(download.audiobookId);
    logger.log(`🔓 Released lock for audiobook ${download.audiobookId}`);

    // Update status
    download.status = 'failed';
    download.error = error;

    // Remove from active downloads after a delay
    setTimeout(() => {
      this.activeDownloads.delete(taskId);
      this.notifyListeners();
    }, 5000);

    this.notifyListeners();
  }

  /**
   * Cancel a download
   */
  cancelDownload(taskId) {
    const download = this.activeDownloads.get(taskId);
    if (!download) return;

    logger.log(`🛑 Cancelling video generation: ${taskId}`);

    // Clear interval (safe to call multiple times)
    if (download.pollInterval) {
      clearInterval(download.pollInterval);
      download.pollInterval = null;
    }

    // Release lock
    this.audiobookLocks.delete(download.audiobookId);
    logger.log(`🔓 Released lock for audiobook ${download.audiobookId}`);

    // Remove from active downloads
    this.activeDownloads.delete(taskId);
    this.notifyListeners();
  }

  /**
   * Get all active downloads
   */
  getActiveDownloads() {
    return Array.from(this.activeDownloads.entries()).map(([taskId, data]) => ({
      taskId,
      ...data,
    }));
  }

  /**
   * Subscribe to download updates
   */
  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Notify all listeners of changes
   */
  notifyListeners() {
    const downloads = this.getActiveDownloads();
    this.listeners.forEach(callback => {
      try {
        callback(downloads);
      } catch (error) {
        logger.error('Error in download listener:', error);
      }
    });
  }

  /**
   * Clean up all downloads (e.g., on sign out)
   */
  cleanup() {
    logger.log('🧹 Cleaning up all video downloads');

    // Clear all intervals
    this.activeDownloads.forEach(download => {
      if (download.pollInterval) {
        clearInterval(download.pollInterval);
      }
    });

    // Clear map and locks
    this.activeDownloads.clear();
    this.audiobookLocks.clear();
    this.notifyListeners();
  }
}

// Export singleton instance
const videoDownloadManager = new VideoDownloadManager();
export default videoDownloadManager;
